// src/api/v3/customers-api-v3.js

import { manualDelete } from './manual-delete-api-v3';

// ----------------- env & URL helpers -----------------
const fromVite = (k) =>
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[k]) || undefined;
const fromCRA = (k) =>
  (typeof process !== 'undefined' && process.env && process.env[k]) || undefined;
const pickEnv = (viteKey, craKey) => fromVite(viteKey) ?? fromCRA(craKey);

function ensureScheme(u = '') {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  // allow protocol-relative (rare)
  if (u.startsWith('//')) return `https:${u}`;
  const isLocal = /^(localhost|127\.0\.0\.1|\[::1\])(:\d+)?$/i.test(u) || u.startsWith('localhost:') || u.startsWith('127.0.0.1:');
  return `${isLocal ? 'http' : 'https'}://${u}`;
}
function trimSlashes(s = '') {
  return s.replace(/\/+$/, '');
}
function leadingSlash(s = '') {
  if (!s) return '/';
  return s.startsWith('/') ? s : `/${s}`;
}

// Base (host) and prefix (/api/v3)
const RAW_API_BASE   = pickEnv('VITE_API_BASE', 'REACT_APP_API_BASE');
const RAW_API_PREFIX = pickEnv('VITE_API_PREFIX', 'REACT_APP_API_PREFIX') || '/api/v3';

// Guess a sensible default base if none provided
function guessBase() {
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1' || h === '[::1]') return 'http://localhost:3001';
  }
  // Production fallback
  return 'https://api.bagspa.belleandkate.com';
}

export const API_BASE   = trimSlashes(ensureScheme(RAW_API_BASE || guessBase()));
export const API_PREFIX = trimSlashes(leadingSlash(RAW_API_PREFIX));

// Build absolute URL safely.
// - If p is absolute http(s) -> return as-is
// - If p starts with /api/   -> base + p
// - Else                     -> base + API_PREFIX + p
function buildPath(p = '') {
  if (!p) return API_PREFIX;
  if (/^https?:\/\//i.test(p)) return p;
  if (p.startsWith('/api/')) return p;
  return `${API_PREFIX}${leadingSlash(p)}`;
}

export const B = (p = '') => new URL(buildPath(p), `${API_BASE}/`).toString();

// ----------------- payload helpers -----------------
export function unwrapItems(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.data))  return payload.data;
  if (payload.data && Array.isArray(payload.data.items)) return payload.data.items;
  return [];
}

export function unwrapItem(payload) {
  if (!payload) return null;
  if (payload.item) return payload.item;
  if (payload.data && !Array.isArray(payload.data)) return payload.data;
  if (payload.id) return payload; // raw object
  return null;
}

function isFormData(body) {
  return body && typeof FormData !== 'undefined' && body instanceof FormData;
}

async function safeParseJSON(res) {
  if (res.status === 204) return null;
  const ct = res.headers.get('content-type') || '';
  const text = await res.text();
  if (!text) return null;
  if (!ct.includes('application/json')) {
    throw new Error(`Expected JSON but got "${ct}". First bytes: ${text.slice(0, 200)}`);
  }
  try {
    return JSON.parse(text);
  } catch (e) {
    throw new Error(`Invalid JSON (${e.message}). First bytes: ${text.slice(0, 200)}`);
  }
}

export async function jsonFetch(url, { method = 'GET', body, headers = {}, ...rest } = {}) {
  const finalHeaders = { Accept: 'application/json', ...headers };
  let finalBody = body;

  if (body && !isFormData(body)) {
    finalHeaders['Content-Type'] = finalHeaders['Content-Type'] || 'application/json';
    if (typeof body === 'object') finalBody = JSON.stringify(body);
  }

  const res = await fetch(url, {
    method,
    headers: finalHeaders,
    credentials: 'include', // keep cookies/session
    body: finalBody,
    ...rest,
  });

  if (!res.ok) {
    let more = '';
    try { more = (await res.text()).slice(0, 300); } catch {}
    throw new Error(`API ${method} ${url} failed: ${res.status} ${res.statusText} ${more}`);
  }

  return safeParseJSON(res);
}

// small helper to attach query params
function withParams(urlString, params = {}) {
  const url = new URL(urlString);
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) {
      v.forEach((vv) => url.searchParams.append(k, String(vv)));
    } else {
      url.searchParams.set(k, String(v));
    }
  });
  return url.toString();
}

// ----------------- APIs -----------------
export const CustomersAPI = {
  /**
   * list({ q, page, per, sort_by, sort_dir })
   * sort_by: id | name | email | created_at
   * sort_dir: asc | desc
   */
  list: (opts = {}) => {
    const {
      q = '',
      page = 1,
      per = 25,
      sort_by = 'id',
      sort_dir = 'desc',
    } = opts;

    const url = withParams(B('/customers'), { q, page, per, sort_by, sort_dir });
    return jsonFetch(url);
  },

  show:   (id)          => jsonFetch(B(`/customers/${id}`)),
  create: (payload)     => jsonFetch(B('/customers'),       { method: 'POST', body: { customer: payload } }),
  update: (id, payload) => jsonFetch(B(`/customers/${id}`), { method: 'PUT',  body: { customer: payload } }),
  destroy:(id)          => manualDelete(B(`/customers/${id}`)),
  exportCSV:   ()               => jsonFetch(B(`/customers/export.csv`)),
  exportXLSX:   ()               => jsonFetch(B(`/customers/export.csv`)),
};

export const CustomerAddressesAPI = {
  list:   (customerId)               => jsonFetch(B(`/customers/${customerId}/addresses`)),
  show:   (customerId, id)           => jsonFetch(B(`/customers/${customerId}/addresses/${id}`)),
  create: (customerId, payload)      => jsonFetch(B(`/customers/${customerId}/addresses`),       { method: 'POST', body: { address: payload } }),
  update: (customerId, id, payload)  => jsonFetch(B(`/customers/${customerId}/addresses/${id}`), { method: 'PUT',  body: { address: payload } }),
  destroy:(customerId, id)           => jsonFetch(B(`/customers/${customerId}/addresses/${id}`), { method: 'DELETE' }),
};

// ✅ Default export (back-compat): import API from '...'; API.list(...)
const apiDefault = CustomersAPI;
export default apiDefault;
